import { EnvironmentInjector, Injectable, inject } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  AdminServiceSearchSyncResponse,
  CollectionNames,
  SearchSyncStatus,
  SearchSyncStatusSchema,
  enumEquals,
} from '@vmspdb/definitions';
import { GlobalErrorHandlerService } from '@vmspdb/ui/common';
import { ConnectAdmin } from '@vmspdb/ui/connect';
import { AuthRunEffects } from '@vmspdb/ui/store';
import { forkJoin, of, tap } from 'rxjs';
import { catchError, concatMap, map, switchMap } from 'rxjs/operators';
import { SettingsRepositoryService, withSearchSync } from '../../repositories';
import { VOID } from '../../shared/utils';
import * as SettingsActions from './settings.actions';

@Injectable()
export class SettingsEffects extends AuthRunEffects {
  private admin = inject(ConnectAdmin);

  private repository = inject(SettingsRepositoryService);
  private injector = inject(EnvironmentInjector);
  private toastr = inject(NbToastrService);
  private errors = inject(GlobalErrorHandlerService);
  loadSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SettingsActions.loadSettings),
      withSearchSync(CollectionNames.Settings, this.injector),
      concatMap(() =>
        this.repository.load().pipe(
          map((data) =>
            SettingsActions.loadSettingsSuccess({ settings: data }),
          ),
          catchError((error) => {
            this.errors.reportCatastrophicError(error, 'Settings Load');
            return of(SettingsActions.loadSettingsFailure({ error }));
          }),
        ),
      ),
    );
  });

  saveDropboxSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SettingsActions.saveDropboxSettings),
      switchMap(({ settings }) =>
        this.repository.updateDropbox(settings).pipe(
          map(() => SettingsActions.loadSettings()),
          catchError((error) => {
            this.errors.handleError(error);
            return of(SettingsActions.saveDropboxSettingsFailure({ error }));
          }),
        ),
      ),
    );
  });

  saveProjectPermitSettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SettingsActions.saveProjectPermitSettings),
      switchMap(({ settings }) =>
        this.repository.updateProjectPermit(settings).pipe(
          map(() => SettingsActions.loadSettings()),
          catchError((error) => {
            this.errors.handleError(error);
            return of(
              SettingsActions.saveProjectPermitSettingsFailure({ error }),
            );
          }),
        ),
      ),
    );
  });

  saveProjectSurveySettings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SettingsActions.saveProjectSurveySettings),
      switchMap(({ settings }) =>
        this.repository.updateProjectSurvey(settings).pipe(
          map(() => SettingsActions.loadSettings()),
          catchError((error) => {
            this.errors.reportCaughtError(error);
            return of(
              SettingsActions.saveProjectSurveySettingsFailure({ error }),
            );
          }),
        ),
      ),
    );
  });

  syncSearch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SettingsActions.syncSearch),
      switchMap(({ reset }) =>
        forkJoin([
          this.admin.searchSync({
            reset,
          }),
        ]),
      ),
      tap(([response]) => this.reportSearchSyncRequest(response)),
      catchError((err) => {
        this.errors.handleError(err);
        this.toastr.danger(err, 'Error');
        return VOID;
      }),
      map(() => SettingsActions.loadSettings()),
    );
  });

  constructor() {
    const actions$ = inject(Actions);

    super(actions$);
  }

  /**
   * Reports what the server did with the re-index *request*. Nothing has been
   * indexed at this point — the response only says whether the work was
   * dispatched, is queued behind a run already in progress, or was refused —
   * so none of these messages may claim the index has been updated.
   *
   * `status` is `UNSPECIFIED` against a server predating the field, in which
   * case `queued` remains the accept signal and the wording falls back to the
   * original, minus its success claim.
   */
  private reportSearchSyncRequest(response: AdminServiceSearchSyncResponse) {
    const reference = response.messageId
      ? ` Reference: ${response.messageId}.`
      : '';

    const status = (value: SearchSyncStatus) =>
      enumEquals(SearchSyncStatusSchema, response.status, value);

    if (status(SearchSyncStatus.REFUSED) || !response.queued) {
      this.toastr.danger(
        `The re-index request was refused, so nothing has been queued.${reference}`,
        'Re-index refused',
      );

      return;
    }

    if (status(SearchSyncStatus.WAITING)) {
      this.toastr.info(
        `Re-index requested. A re-index is already in progress, so this one is queued behind it and will start when that run finishes.${reference}`,
        'Re-index queued',
      );

      return;
    }

    if (status(SearchSyncStatus.STARTED)) {
      this.toastr.success(
        `Re-index requested and now running. This may take a minute to complete.${reference}`,
        'Re-index started',
      );

      return;
    }

    this.toastr.success(
      `Re-index requested. This may take a minute to complete.${reference}`,
      'Re-index requested',
    );
  }
}
